// The enlarged view is a fresh snapshot of the currently rendered composition.
// Product records, variant selection and cart properties remain the source of truth.
const NS = 'http://www.w3.org/2000/svg';
const node = (tag, attrs = {}) => {
  const element = document.createElementNS(NS, tag);
  for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, String(value));
  return element;
};

function wrapPreviewText(value, maxCharacters) {
  return value.split('\n').flatMap((paragraph) => {
    if (!paragraph.trim()) return [''];
    const output = [];
    let line = '';
    for (const word of paragraph.trim().split(/\s+/)) {
      const chunks = [];
      let chunk = '';
      for (const character of Array.from(word)) {
        if (Array.from(chunk).length >= maxCharacters) {
          chunks.push(chunk);
          chunk = '';
        }
        chunk += character;
      }
      if (chunk) chunks.push(chunk);
      for (const part of chunks) {
        const candidate = line ? `${line} ${part}` : part;
        if (Array.from(candidate).length > maxCharacters && line) {
          output.push(line);
          line = part;
        } else {
          line = candidate;
        }
      }
    }
    output.push(line);
    return output;
  });
}

export function appendPreviewText(svg, value, box, fill = '#273048', options = {}) {
  if (!value?.trim()) return;
  const hasCustomStyle = Number.isFinite(Number(options.fontSizePt));
  const requestedSize = hasCustomStyle ? Number(options.fontSizePt) * (96 / 72) : 0;
  const lineHeight = hasCustomStyle ? Math.max(0.9, Math.min(1.6, Number(options.lineHeight) || 1.2)) : 1.2;
  const letterSpacing = hasCustomStyle ? Math.max(-0.02, Math.min(0.15, Number(options.letterSpacing) || 0)) : 0;
  const widthFactor = 0.62 + Math.max(0, letterSpacing);
  const charactersPerLine = hasCustomStyle
    ? Math.max(1, Math.floor(box.width / (requestedSize * widthFactor)))
    : 28;
  const lines = hasCustomStyle
    ? wrapPreviewText(value.trim(), charactersPerLine)
    : value.trim().split('\n').flatMap((line) => {
      const words = line.match(/.{1,28}(?:\s|$)|.{1,28}/gu) || [''];
      return words.map((word) => word.trim());
    });
  const longestLine = Math.max(1, ...lines.map((line) => Array.from(line).length));
  const size = hasCustomStyle
    ? Math.min(requestedSize, box.width / (longestLine * widthFactor), box.height / (lines.length * lineHeight))
    : Math.min(box.width / 15, box.height / (lines.length * 1.3));
  const alignments = { left: 'start', center: 'middle', right: 'end' };
  const alignment = hasCustomStyle && Object.hasOwn(alignments, options.alignment) ? options.alignment : 'center';
  const anchor = alignments[alignment];
  const x = box.x + (alignment === 'left' ? -box.width / 2 : alignment === 'right' ? box.width / 2 : 0);
  const fontFamily = hasCustomStyle && ['Assistant', 'Heebo', 'Rubik'].includes(options.fontFamily)
    ? options.fontFamily
    : 'Arial';
  const firstStrong = value.match(/[\u0590-\u05ff]|[A-Za-z]/u)?.[0] || '';
  const direction = /[\u0590-\u05ff]/u.test(firstStrong) ? 'rtl' : 'ltr';
  const group = node('g', { transform: `rotate(${box.angle || 0} ${box.x} ${box.y})` });
  const text = node('text', {
    x, y: box.y - ((lines.length - 1) * size * lineHeight) / 2,
    'text-anchor': anchor, 'dominant-baseline': 'middle', direction, fill,
    'font-family': hasCustomStyle ? `"${fontFamily}", Arial, sans-serif` : 'Arial, sans-serif',
    'font-size': size, 'letter-spacing': `${letterSpacing}em`,
    'unicode-bidi': 'plaintext', 'data-kp-preview-text': ''
  });
  lines.forEach((line, index) => {
    const span = node('tspan', { x, dy: index ? size * lineHeight : 0 });
    span.textContent = line;
    text.append(span);
  });
  group.append(text);
  svg.append(group);
}

export function updateShawlText(figure, value, hasArtwork) {
  if (!figure) return;
  let svg = figure.querySelector('.kp-shawl-text-preview');
  if (!svg) {
    svg = node('svg', { viewBox: '0 0 1536 2048', 'aria-hidden': 'true' });
    svg.classList.add('kp-shawl-text-preview');
    Object.assign(svg.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', pointerEvents: 'none' });
    figure.append(svg);
  }
  svg.replaceChildren();
  const placement = figure.querySelector('[data-placement]');
  if (placement) {
    placement.style.top = value?.trim() && hasArtwork ? '61.5113%' : '64.1113%';
    placement.style.height = value?.trim() && hasArtwork ? '12.1338%' : '17.334%';
  }
  appendPreviewText(svg, value, { x: 800, y: hasArtwork ? 1420 : 1313, width: 355, height: hasArtwork ? 120 : 355, angle: 21.5 });
}

function highResolution(src) {
  if (!src || /^(blob:|data:)/.test(src)) return src;
  const url = new URL(src, location.href);
  // Keep the exact selected image identity; never substitute the original media URL.
  if (url.pathname.includes('/files/') && (url.hostname === location.hostname || url.hostname.endsWith('shopify.com'))) {
    url.searchParams.set('width', '3840');
  }
  return url.href;
}

function composition(source) {
  const composite=source.querySelector('.kpt-gallery-artwork');
  if(composite){
    const svg=composite.cloneNode(true);
    svg.removeAttribute('class');svg.removeAttribute('style');
    svg.setAttribute('preserveAspectRatio','xMidYMid meet');
    svg.setAttribute('role','img');svg.setAttribute('aria-label','Current personalized product preview');
    const base=svg.querySelector('image');if(base)base.setAttribute('href',highResolution(base.getAttribute('href')));
    return svg;
  }

  const image = source.querySelector('img.product-media__image, .kp-shawl-personalizer__base');
  if (!image) return null;
  const w = image.naturalWidth || Number(image.getAttribute('width')) || 3024;
  const h = image.naturalHeight || Number(image.getAttribute('height')) || 4032;
  const viewBox = `0 0 ${w} ${h}`;
  const svg = node('svg', { viewBox, role: 'img', 'aria-label': image.alt || 'Selected product preview', preserveAspectRatio: 'xMidYMid meet' });
  const src = image.hasAttribute('srcset') ? image.currentSrc || image.src : image.src;
  const base = node('image', { href: src, width: w, height: h, preserveAspectRatio: 'xMidYMid meet' });
  svg.append(base);
  const highRes = highResolution(src);
  if (highRes !== src) {
    const full = new Image();
    full.onload = () => { if (svg.isConnected) base.setAttribute('href', highRes); };
    full.src = highRes;
  }
  const bounds = source.getBoundingClientRect();
  const rect = image.getBoundingClientRect();
  const css = getComputedStyle(image);
  const scale = css.objectFit === 'cover' ? Math.max(rect.width / w, rect.height / h) : Math.min(rect.width / w, rect.height / h);
  const sx = css.objectFit === 'fill' ? rect.width / w : scale;
  const sy = css.objectFit === 'fill' ? rect.height / h : scale;
  const pos = css.objectPosition.split(' ').map((v) => v.endsWith('%') ? parseFloat(v) / 100 : .5);
  const left = rect.left - bounds.left + (rect.width - w * sx) * (pos[0] ?? .5);
  const top = rect.top - bounds.top + (rect.height - h * sy) * (pos[1] ?? .5);
  for (const overlay of source.querySelectorAll(':scope > .kp-ready-artwork-preview, :scope > .kp-custom-artwork-preview,  :scope > .kp-shawl-text-preview, :scope > .kpt-ready-extra-artwork, :scope > .kpt-shawl-artwork')) {
    const view = overlay.viewBox.baseVal;
    const group = node('g', { transform: `translate(${-left / sx} ${-top / sy}) scale(${bounds.width / view.width / sx} ${bounds.height / view.height / sy})` });
    for (const child of overlay.children) group.append(child.cloneNode(true));
    svg.append(group);
  }
  const placement = source.querySelector('[data-placement]:not([hidden])');
  const art = placement?.querySelector('img:not([hidden])');
  if (art?.src) {
    const style = getComputedStyle(placement);
    const x = parseFloat(style.left) / bounds.width * w;
    const y = parseFloat(style.top) / bounds.height * h;
    const width = parseFloat(style.width) / bounds.width * w;
    const height = parseFloat(style.height) / bounds.height * h;
    const matrix = new DOMMatrix(style.transform);
    const angle = Math.atan2(matrix.b, matrix.a) * 180 / Math.PI;
    svg.append(node('image', { href: art.src, x: x - width / 2, y: y - height / 2, width, height, transform: `rotate(${angle} ${x} ${y})`, preserveAspectRatio: 'xMidYMid meet' }));
  }
  return svg;
}

let dialog;
let previousFocus;
let zoom = 1, panX = 0, panY = 0, gesture, dragged = false, openRevision = 0;
const pointers = new Map();
const clampZoom = (value) => Math.max(1, Math.min(3, value));
const resetView = () => {
  zoom = 1; panX = panY = 0; gesture = null; pointers.clear(); dragged = false;
  updateTransform();
};
// Sample the actual displayed base, including browser color conversion. An
// inaccessible/transparent source falls back to the existing product beige.
function imageBackground(image) {
  try {
    if (!image?.complete || !image.naturalWidth) return '#e8d0c0';
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, 1, 1, 0, 0, 1, 1);
    const [r, g, b, alpha] = context.getImageData(0, 0, 1, 1).data;
    return alpha > 250 ? `rgb(${r} ${g} ${b})` : '#e8d0c0';
  } catch { return '#e8d0c0'; }
}

function beginGesture() {
  const points = [...pointers.values()];
  if (!points.length) { gesture = null; return; }
  const bounds = dialog.querySelector('[data-viewer-stage]').getBoundingClientRect();
  const center = { x: bounds.left + bounds.width / 2, y: bounds.top + bounds.height / 2 };
  if (points.length === 1) {
    gesture = { count: 1, x: points[0].x, y: points[0].y, panX, panY };
    return;
  }
  const [a, b] = points;
  const x = (a.x + b.x) / 2, y = (a.y + b.y) / 2;
  gesture = {
    count: 2, zoom, distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)), center,
    anchorX: (x - center.x - panX) / zoom,
    anchorY: (y - center.y - panY) / zoom
  };
}
const updateTransform = () => {
  const canvas = dialog.querySelector('[data-viewer-canvas]');
  const limitX = canvas.clientWidth * (zoom - 1) / 2;
  const limitY = canvas.clientHeight * (zoom - 1) / 2;
  panX = Math.max(-limitX, Math.min(limitX, panX));
  panY = Math.max(-limitY, Math.min(limitY, panY));
  canvas.style.transform = `translate(${panX}px, ${panY}px) scale(${zoom})`;
  dialog.querySelector('[data-viewer-reset]').textContent = zoom <= 1.001 ? 'Fit' : `${Math.round(zoom * 100)}% · Fit`;
  dialog.querySelector('[data-viewer-minus]').disabled = zoom <= 1.001;
  dialog.querySelector('[data-viewer-plus]').disabled = zoom >= 2.999;
  dialog.dataset.zoomed = String(zoom > 1);
};
function ensureDialog() {
  if (dialog) return;
  dialog = document.createElement('dialog');
  dialog.className = 'kp-composite-viewer';
  dialog.setAttribute('aria-label', 'Enlarged product preview');
  dialog.setAttribute('aria-describedby', 'kp-viewer-proof-note');
  dialog.innerHTML = `<div class="kp-composite-viewer__toolbar"><div><button type="button" data-viewer-minus aria-label="Zoom out">−</button><button type="button" data-viewer-reset>Fit</button><button type="button" data-viewer-plus aria-label="Zoom in">+</button></div><button type="button" data-viewer-close aria-label="Close enlarged preview">✕</button></div><div class="kp-composite-viewer__stage" data-viewer-stage><div data-viewer-canvas></div></div><p class="kp-composite-viewer__note">Approximate preview. Personalized orders receive a digital proof for approval.</p>`;
  document.body.append(dialog);
  dialog.querySelector('[data-viewer-close]').addEventListener('click', () => dialog.close());
  dialog.querySelector('.kp-composite-viewer__note').id = 'kp-viewer-proof-note';
  dialog.querySelector('[data-viewer-plus]').addEventListener('click', () => { zoom = clampZoom(zoom + .5); updateTransform(); });
  dialog.querySelector('[data-viewer-minus]').addEventListener('click', () => { zoom = clampZoom(zoom - .5); updateTransform(); });
  dialog.querySelector('[data-viewer-reset]').addEventListener('click', resetView);
  dialog.addEventListener('click', (event) => {
    if (dragged) { dragged = false; return; }
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('kp-viewer-open');
    dialog.querySelector('[data-viewer-canvas]').replaceChildren();
    previousFocus?.focus({ preventScroll: true });
    pointers.clear(); gesture = null; dragged = false; openRevision += 1;
  });
  const stage = dialog.querySelector('[data-viewer-stage]');
  stage.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!pointers.size) dragged = false;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    stage.setPointerCapture(event.pointerId);
    beginGesture();
    event.preventDefault();
  });
  stage.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId) || !gesture) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointers.values()];
    if (points.length >= 2 && gesture.count === 2) {
      const [a, b] = points;
      zoom = clampZoom(gesture.zoom * Math.hypot(b.x - a.x, b.y - a.y) / gesture.distance);
      panX = (a.x + b.x) / 2 - gesture.center.x - gesture.anchorX * zoom;
      panY = (a.y + b.y) / 2 - gesture.center.y - gesture.anchorY * zoom;
      dragged = true;
    } else if (zoom > 1 && gesture.count === 1) {
      const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
      if (Math.hypot(dx, dy) > 3) dragged = true;
      panX = gesture.panX + dx; panY = gesture.panY + dy;
    }
    updateTransform();
    event.preventDefault();
  });
  const endPointer = (event) => { pointers.delete(event.pointerId); beginGesture(); };
  stage.addEventListener('pointerup', endPointer);
  stage.addEventListener('pointercancel', endPointer);
  stage.addEventListener('lostpointercapture', endPointer);
  window.addEventListener('resize', () => { if (dialog.open) resetView(); });
  window.addEventListener('pagehide', () => { if (dialog.open) dialog.close(); document.documentElement.classList.remove('kp-viewer-open'); });
}

function open(source, trigger) {
  document.dispatchEvent(new Event('kp:preview-refresh'));
  const svg = composition(source);
  if (!svg) return false;
  ensureDialog();
  const revision = ++openRevision;
  const sourceImage = source.querySelector('img.product-media__image, .kp-shawl-personalizer__base');
  dialog.style.backgroundColor = imageBackground(sourceImage);
  sourceImage?.addEventListener('load', () => {
    if (dialog.open && revision === openRevision) dialog.style.backgroundColor = imageBackground(sourceImage);
  }, { once: true });
  previousFocus = trigger.closest('button,[tabindex]') || document.activeElement;
  dialog.querySelector('[data-viewer-canvas]').replaceChildren(svg);
  zoom = 1; panX = panY = 0; pointers.clear(); gesture = null; dragged = false;
  dialog.showModal();
  document.documentElement.classList.add('kp-viewer-open');
  updateTransform();
  dialog.querySelector('[data-viewer-close]').focus();
  return true;
}

window.addEventListener('click', (event) => {
  if (!(event.target instanceof Element) || event.target.closest('dialog')) return;
  const source = event.target.closest('main media-gallery .product-media-container--zoomable, main media-gallery .product-media-container--image, main media-gallery [data-shawl-preview]');
  if (!source || event.target.closest('a,slideshow-controls,slideshow-arrows')) return;
  if (open(source, event.target)) { event.preventDefault(); event.stopImmediatePropagation(); }
}, true);

const style = document.createElement('style');
style.textContent = `html.kp-viewer-open{overflow:hidden}.kp-composite-viewer{width:calc(100vw - 24px);height:calc(100dvh - 24px);max-width:1400px;max-height:1100px;margin:auto;padding:0;border:0;border-radius:16px;background:#f4eae2;color:#273be2;overflow:hidden}.kp-composite-viewer[open]{display:grid;grid-template-rows:60px minmax(0,1fr) auto}.kp-composite-viewer::backdrop{background:rgb(14 17 40 / .75)}.kp-composite-viewer__toolbar{display:flex;align-items:center;justify-content:space-between;padding:8px 12px;position:relative;z-index:2}.kp-composite-viewer button{min-width:44px;min-height:44px;padding:8px 14px;color:#273be2;background:transparent;border:1px solid #273be233;border-radius:999px;font:inherit;cursor:pointer}.kp-composite-viewer button:disabled{opacity:.4;cursor:default}.kp-composite-viewer button:focus-visible{outline:3px solid #273be2;outline-offset:2px}.kp-composite-viewer__toolbar>div{display:flex;gap:7px}.kp-composite-viewer__stage{position:relative;min-height:0;overflow:hidden;padding:8px;touch-action:none}.kp-composite-viewer [data-viewer-canvas]{position:absolute;inset:8px;width:calc(100% - 16px);height:calc(100% - 16px);min-height:0;transform-origin:center;will-change:transform}.kp-composite-viewer svg{display:block;width:100%;height:100%;overflow:visible}.kp-composite-viewer[data-zoomed=true] .kp-composite-viewer__stage{cursor:grab}.kp-composite-viewer__note{font-size:12px;font-weight:700;line-height:1.45;width:fit-content;max-width:calc(100% - 24px);margin:8px auto 12px;padding:10px 16px;text-align:center;border:1px solid #273be22b;border-radius:16px;background:#f4eae2;cursor:default}.kp-shawl-enlarge{position:absolute;inset:0;border:0;background:transparent;cursor:zoom-in;width:100%;height:100%;border-radius:inherit}.kp-shawl-enlarge:focus-visible{outline:3px solid #273be2;outline-offset:-5px}@media(max-width:749px){.kp-composite-viewer{width:100vw;height:100dvh;max-height:none;border-radius:0}.kp-composite-viewer__note{margin-bottom:max(12px,env(safe-area-inset-bottom));padding:10px 14px}}`;
document.head.append(style);
